<template>
  <div class="services-container">
    <div
      v-for="(group, groupIndex) in serviceGroups"
      :key="groupIndex"
      class="service-group"
    >
      <div class="service-group__header">
        <div class="service-group__header-icon">
          <i :class="group.icon" />
        </div>
        <div class="service-group__header-content">
          <h3 class="service-group__title">
            {{ t(group.title) }}
          </h3>
          <p class="service-group__description">
            {{ t(group.description) }}
          </p>
        </div>
      </div>

      <div class="service-group__items">
        <div
          v-for="(item, index) in group.services"
          :key="index"
          class="service-card"
          :class="{ 'service-card--disabled': getServiceFlag(item.name)?.enabled === false }"
        >
          <div class="service-card__content">
            <div class="service-card__icon">
              <i :class="item.icon" />
            </div>
            <div class="service-card__info">
              <h4 class="service-card__title">
                {{ t(item.title) }}
                <ComingSoonBadge
                  v-if="getServiceFlag(item.name)?.comingSoon"
                  variant="inline"
                />
              </h4>
              <p class="service-card__subtitle">
                {{ t(item.subtitle) }}
              </p>
            </div>
          </div>
          <button
            v-if="getServiceFlag(item.name)?.enabled !== false"
            class="service-card__button"
            @click="handleConfigure(item.name)"
          >
            {{ t('storeParameters.services.configure') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <Dialog
    v-if="selectedServiceParameter"
    v-model:visible="displayServiceModal"
    class="service-parameter"
    modal
    :style="{ width: selectedServiceParameter.modalWidth }"
    @update:visible="handleClose"
  >
    <template #header>
      <div
        v-if="selectedServiceParameter.component === 'WhatsappBusinessServiceParameter'"
        class="whatsapp-dialog-header"
      >
        <div class="whatsapp-dialog-header__icon">
          <i class="fa-brands fa-whatsapp" />
        </div>
        <div class="whatsapp-dialog-header__content">
          <h3>{{ t(selectedServiceParameter.title) }}</h3>
        </div>
      </div>
      <span
        v-else
        class="p-dialog-title"
      >
        {{ serviceModalHeader }}
      </span>
    </template>
    <component
      :is="getServiceComponent(selectedServiceParameter.component)"
      :visible="displayServiceModal"
      :shop-id="shopId"
      :shop-url="shopUrl"
      @close="handleClose"
      @whatsapp-account-connected="onWhatsappAccountConnected"
    />
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  defineAsyncComponent,
} from 'vue';

import Dialog from 'primevue/dialog';

import { ServiceParameter } from '@/types/store-parameters-types';
import { getFeatureFlagForService } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

import { useI18n } from 'vue-i18n';

interface ServiceGroup {
  title: string;
  description: string;
  icon: string;
  services: ServiceParameter[];
}

export default defineComponent({
  name: 'NewServicesParameters',

  components: {
    Dialog,
    ComingSoonBadge,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    shopUrl: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const displayServiceModal = ref(false);
    const selectedServiceParameter = ref<null | ServiceParameter>(null);
    const isWhatsappAccountConnected = ref(false);

    // Define service groups
    const serviceGroups = ref<ServiceGroup[]>([
      {
        title: 'storeParameters.services.groups.sendingChannels.title',
        description: 'storeParameters.services.groups.sendingChannels.description',
        icon: 'fal fa-paper-plane',
        services: [
          {
            name: 'email',
            title: 'storeParameters.services.emailServiceParameter.title',
            subtitle: 'storeParameters.services.emailServiceParameter.subtitle',
            component: 'EmailServiceParameter',
            icon: 'fal fa-envelope',
            modalWidth: '100rem',
          },
          {
            name: 'sms',
            title: 'storeParameters.services.smsServiceParameter.title',
            subtitle: 'storeParameters.services.smsServiceParameter.subtitle',
            component: 'SmsServiceParameter',
            icon: 'fal fa-message-sms',
            modalWidth: '100rem',
          },
          {
            name: 'push_notification',
            title: 'storeParameters.services.pushNotificationServiceParameter.title',
            subtitle: 'storeParameters.services.pushNotificationServiceParameter.subtitle',
            component: 'PushNotificationServiceParameter',
            icon: 'fal fa-bell',
            modalWidth: '100rem',
          },
          {
            name: 'whatsapp_business',
            title: 'storeParameters.services.whatsappBusinessParameter.title',
            subtitle: 'storeParameters.services.whatsappBusinessParameter.subtitle',
            manage: 'storeParameters.services.whatsappBusinessParameter.manage',
            component: 'WhatsappBusinessServiceParameter',
            icon: 'fa-brands fa-whatsapp',
            modalWidth: '64rem',
          },
          {
            name: 'rcs',
            title: 'storeParameters.services.rcsServiceParameter.title',
            subtitle: 'storeParameters.services.rcsServiceParameter.subtitle',
            manage: 'storeParameters.services.rcsServiceParameter.manage',
            component: 'RcsServiceParameter',
            icon: 'fa-light fa-message-lines',
            modalWidth: '64rem',
          },
        ],
      },
      {
        title: 'storeParameters.services.groups.generalSettings.title',
        description: 'storeParameters.services.groups.generalSettings.description',
        icon: 'fal fa-cog',
        services: [
          {
            name: 'important_links',
            title: 'storeParameters.services.importantLinksServiceParameter.title',
            subtitle: 'storeParameters.services.importantLinksServiceParameter.subtitle',
            component: 'ImportantLinksServiceParameter',
            icon: 'fal fa-link',
            modalWidth: '50rem',
          },
          {
            name: 'social_network',
            title: 'storeParameters.services.socialNetworkServiceParameter.title',
            subtitle: 'storeParameters.services.socialNetworkServiceParameter.subtitle',
            component: 'SocialNetworkServiceParameter',
            icon: 'fal fa-share-nodes',
            modalWidth: '75rem',
          },
          {
            name: 'subscription_page',
            title: 'storeParameters.services.subscriptionPageParameter.title',
            subtitle: 'storeParameters.services.subscriptionPageParameter.subtitle',
            component: 'SubscriptionPageServiceParameter',
            icon: 'fal fa-file-contract',
            modalWidth: '50rem',
          },
          {
            name: 'vouchers',
            title: 'storeParameters.services.voucherServiceParameter.title',
            subtitle: 'storeParameters.services.voucherServiceParameter.subtitle',
            component: 'VoucherServiceParameter',
            icon: 'fal fa-ticket',
            modalWidth: '50rem',
          },
          {
            name: 'ai_setting',
            title: 'storeParameters.services.aiServiceParameter.title',
            subtitle: 'storeParameters.services.aiServiceParameter.subtitle',
            component: 'AIServiceParameter',
            icon: 'fa-light fa-microchip-ai',
            modalWidth: '50rem',
          },
          {
            name: 'product_recommendations',
            title: 'storeParameters.services.productRecommendationServiceParameter.title',
            subtitle: 'storeParameters.services.productRecommendationServiceParameter.subtitle',
            component: 'ProductRecommendationServiceParameter',
            icon: 'fal fa-star',
            modalWidth: '50rem',
          },
          {
            name: 'utm_analytics',
            title: 'storeParameters.services.utmServiceParameter.title',
            subtitle: 'storeParameters.services.utmServiceParameter.subtitle',
            component: 'UtmServiceParameter',
            icon: 'fal fa-chart-line',
            modalWidth: '50rem',
          },
          {
            name: 'sales_association',
            title: 'storeParameters.services.salesAssociationServiceParameter.title',
            subtitle: 'storeParameters.services.salesAssociationServiceParameter.subtitle',
            component: 'SalesAssociationServiceParameter',
            icon: 'fal fa-cart-shopping',
            modalWidth: '60rem',
          },
        ],
      },
    ]);

    // Find service in all groups
    const findService = (serviceName: string): ServiceParameter | undefined => {
      let foundService: ServiceParameter | undefined;
      serviceGroups.value.some((group) => {
        foundService = group.services.find((item) => item.name === serviceName);
        return foundService !== undefined;
      });
      return foundService;
    };

    const handleConfigure = (serviceName: string) => {
      const service = findService(serviceName);
      if (service) {
        selectedServiceParameter.value = service;
        displayServiceModal.value = true;
      }
    };

    const handleClose = () => {
      displayServiceModal.value = false;
      selectedServiceParameter.value = null;
    };

    const serviceModalHeader = computed(() => {
      if (
        selectedServiceParameter.value
        && selectedServiceParameter.value.component === 'WhatsappBusinessServiceParameter'
        && isWhatsappAccountConnected.value
      ) {
        return t(selectedServiceParameter.value.manage);
      }
      return t(selectedServiceParameter.value?.subtitle ?? '');
    });

    function onWhatsappAccountConnected(value: boolean) {
      isWhatsappAccountConnected.value = value;
    }

    // Dynamic component loader
    const getServiceComponent = (componentName: string) => {
      return defineAsyncComponent(() => import(`@/views/shop/tabs/ServicesParameters/services/${componentName}.vue`)
        .catch(() => {
          // Fallback for components in subdirectories
          return import(`@/views/shop/tabs/ServicesParameters/services/${componentName}/${componentName}.vue`);
        }));
    };

    const getServiceFlag = getFeatureFlagForService;

    return {
      t,
      serviceGroups,
      getServiceFlag,
      selectedServiceParameter,
      displayServiceModal,
      handleConfigure,
      handleClose,
      serviceModalHeader,
      onWhatsappAccountConnected,
      isWhatsappAccountConnected,
      getServiceComponent,
    };
  },
});
</script>

<style lang="scss" scoped>
// Container principal
.services-container {
  display: flex;
  flex-direction: column;
  gap: 3rem;
  width: 100%;
  max-width: 1200px;
}

// Groupe de services
.service-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #f0f0f0;
  }

  &__header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #94c840;
    flex-shrink: 0;

    i {
      font-size: 1.25rem;
      color: white;
    }
  }

  &__header-content {
    flex: 1;
  }

  &__title {
    margin: 0 0 0.25rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-color);
    line-height: 1.3;
  }

  &__description {
    margin: 0;
    font-size: 0.875rem;
    color: var(--text-color-secondary);
    line-height: 1.3;
  }

  &__items {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1rem;
  }
}

// Card de service
.service-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #94c840;
    box-shadow: 0 4px 12px rgba(148, 200, 64, 0.1);
    transform: translateY(-2px);
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    min-width: 0;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    min-width: 48px;
    border-radius: 10px;
    background: #f3f4f6;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: #6b7280;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0 0 0.25rem 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--text-color);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--text-color-secondary);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__button {
    padding: 0.5rem 1rem;
    background: transparent;
    border: none;
    color: #94c840;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    border-radius: 6px;

    &:hover {
      background: rgba(148, 200, 64, 0.1);
      color: #7ba832;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;

    &:hover {
      border-color: #e5e7eb;
      box-shadow: none;
      transform: none;
    }
  }
}

// Responsive
@media (max-width: 768px) {
  .service-group__items {
    grid-template-columns: 1fr;
  }

  .service-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;

    &__button {
      width: 100%;
      text-align: center;
    }
  }
}
</style>

<style lang="scss">
.service-parameter {
  .p-dialog-content {
    text-align: unset !important;
  }

  .p-dialog-header {
    align-items: flex-start !important;
  }

  .p-dialog-header-icons {
    margin-top: 0 !important;
    align-self: flex-start !important;
  }

  .p-dialog-title {
    align-self: center !important;
  }

  .whatsapp-dialog-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
      flex-shrink: 0;

      i {
        font-size: 1.75rem;
        color: white;
      }
    }

    &__content {
      flex: 1;

      h3 {
        margin: 0 0 0.25rem 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--text-color);
        line-height: 1.3;
      }

      .subtitle {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--text-color-secondary);
        line-height: 1.3;
      }
    }
  }
}
</style>
